import { isBefore, parseISO, startOfToday } from 'date-fns'

import type { PostCard_PostData } from '~publish/components/PostCard/PostCard'
import type { PostTab } from '~publish/components/PostTabs'
import type { PostStatus } from '~publish/components/graphqlEnums'

// API currently supports array of statuses, and we do mapping here between the tabs and statuses
export const postStatusByTab: Record<PostTab, PostStatus[]> = {
  drafts: ['draft'],
  queue: ['scheduled', 'error'],
  approvals: ['needs_approval'],
  sent: ['sent', 'sending'],
}

const postTabs: readonly PostTab[] = ['drafts', 'queue', 'approvals', 'sent']

export const mapPostStatusToTab = (status: PostStatus): PostTab => {
  for (const tab of postTabs) {
    if (postStatusByTab[tab].includes(status)) {
      return tab
    }
  }

  return 'queue'
}

export function isPausedPost(
  post: PostCard_PostData,
): post is PostCard_PostData & {
  dueAt: string
  status: 'scheduled' | 'error'
} {
  return isQueuedPost(post) && isUnscheduledOrInThePast(post)
}

export function isScheduledPost(
  post: PostCard_PostData,
): post is PostCard_PostData & {
  dueAt: string
  status: 'scheduled'
} {
  return isQueuedPost(post) && !isUnscheduledOrInThePast(post)
}

export function isUnscheduledDraft(
  post: PostCard_PostData,
): post is PostCard_PostData & {
  dueAt: null
  status: 'draft'
} {
  const { status, dueAt } = post
  return postStatusByTab.drafts.includes(status) && !dueAt
}

export function isUnscheduledApproval(
  post: PostCard_PostData,
): post is PostCard_PostData & {
  dueAt: null
  status: 'needs_approval'
} {
  const { status, dueAt } = post
  return postStatusByTab.approvals.includes(status) && !dueAt
}

export function isScheduledPostOrDraft(
  post: PostCard_PostData,
): post is PostCard_PostData & { dueAt: string } {
  return (
    !isUnscheduledDraft(post) &&
    !isUnscheduledApproval(post) &&
    !isPausedPost(post)
  )
}

/**
 * Gets the appropriate display date for a post based on its status
 * For sent posts, use sentAt if available, otherwise fallback to dueAt
 * For other posts, use dueAt
 */
type PostWithOptionalDueDate = {
  status: PostStatus
  sentAt?: string | null
  dueAt?: string | null
}

type PostWithDueDate = PostWithOptionalDueDate & { dueAt: string }

export function getPostDisplayDate(post: PostWithDueDate): string
export function getPostDisplayDate(
  post: PostWithOptionalDueDate,
): string | null | undefined
export function getPostDisplayDate(
  post: PostWithOptionalDueDate,
): string | null | undefined {
  if ((post.status === 'sent' || post.status === 'sending') && post.sentAt) {
    return post.sentAt
  }
  return post.dueAt
}

// Internal helpers
function isQueuedPost(post: PostCard_PostData): post is PostCard_PostData & {
  status: 'scheduled' | 'error'
} {
  const { status } = post
  return postStatusByTab.queue.includes(status)
}

function isUnscheduledOrInThePast(post: PostCard_PostData): boolean {
  const { dueAt } = post
  const isScheduledInThePast =
    !!dueAt && isBefore(parseISO(dueAt), startOfToday())
  const isUnscheduled = dueAt === null

  return isScheduledInThePast || isUnscheduled
}
